import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { cx } from '@orn/shared-web/utils/cx';
import isPinnedToBottom from '@orn/shared-web/utils/scrollPin';
import * as React from 'react';

import {
  AssistantInput,
  AssistantMessage,
  ConnectionIndicator,
  ThinkingIndicator,
  ToolCallLine,
} from '../../components/assistant';
import {
  buildTranscriptDividers,
  clusterTranscriptRows,
  computeGroupedMessageIds,
  isRenderableMessage,
} from '../../lib/assistant/messageDisplay';

import AssistantPaneEmptyState from './AssistantPaneEmptyState';

import type { ConnectionStatus, IConversationMessage } from '../../hooks/useAssistantWebSocket';
import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';

// Safety cap on how long scroll events stay suppressed during a smooth
// send-follow. The suppression normally lifts the instant the glide reaches the
// bottom; this only matters if the animation is interrupted (e.g. the user
// grabs the scrollbar) and never emits an at-bottom event — without the cap,
// pin tracking would be stranded.
const SMOOTH_FOLLOW_SETTLE_TIMEOUT_MS = 1000;

interface IAssistantPaneViewProps {
  messages: IConversationMessage[];
  isProcessing: boolean;
  /** Forwarded to {@link AssistantInput}: true while the live turn is abortable → button is a stop button. */
  isAbortable: boolean;
  isRehydrating: boolean;
  /**
   * True while the turn is suspended waiting on a browser-side client tool (the
   * user is completing the Plaid flow). Suppresses the "Thinking…" indicator —
   * the assistant isn't thinking, it's waiting on the user, and the tool's gray
   * activity line already says what's happening. The turn stays abortable.
   * Defaults to `false` so Storybook fixtures need not set it.
   */
  isAwaitingClientTool?: boolean;
  connectionStatus: ConnectionStatus;
  error: string | null;
  /**
   * Forwarded to {@link AssistantInput}. `attachments` is populated by the
   * composer when the user attached documents to the turn; the hook derives
   * the wire `attachmentDocumentIds` from `attachments.map(a => a.id)` and
   * reuses the same array for the locally-echoed bubble.
   */
  onSend: (content: string, attachments?: IObservationDocument[]) => void;
  /** Forwarded to {@link AssistantInput}: interrupt the in-flight turn. */
  onStop: () => void;
  /**
   * Re-claim the assistant in this tab after it was superseded by another tab
   * (`connectionStatus === 'superseded'`). Wired to the banner's "Reconnect"
   * button.
   */
  onReclaim: () => void;
}

/**
 * Pure presentational view for the assistant pane. Takes the
 * `useAssistantWebSocket` hook's return shape as props so Storybook stories
 * and visual-verification subagents can render every state from fixture
 * data without mocking the hook.
 *
 * Separators render above the bubble that opens them (see
 * {@link buildTranscriptDividers}): a date-stamped rule at each session gap, or
 * the lighter per-minute timestamp otherwise. Empty turns (no content, no
 * attachments) are skipped; tool-call detail is not rendered — a turn in flight
 * shows only a spinner bubble, and the details are surfaced to the console
 * behind the `debug-assistant` gate by {@link AssistantPane}.
 */
const AssistantPaneView = ({
  messages,
  isProcessing,
  isAbortable,
  isRehydrating,
  isAwaitingClientTool = false,
  connectionStatus,
  error,
  onSend,
  onStop,
  onReclaim,
}: IAssistantPaneViewProps) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  // Whether the user is pinned to the bottom. Updated on every scroll so the
  // auto-follow reflects the user's latest intent — scrolling up to read history
  // mid-stream parks this `false` and stops the follow until they return.
  const isPinnedRef = React.useRef(true);
  // One-shot: the next auto-follow should glide rather than jump. Set when the
  // user sends from a scrolled-up position so their bubble eases into view; the
  // effect consumes it on the first run so streaming stays an instant jump.
  const smoothFollowOnceRef = React.useRef(false);
  // While our own smooth send-follow animates, the browser fires a stream of
  // scroll events at intermediate (not-yet-bottom) positions. Those must not be
  // read as the user scrolling away, or `isPinnedRef` would flip false and a
  // delta arriving mid-glide would cancel the follow. Suppress reads until the
  // glide reaches the bottom; `suppressUntilMsRef` is the safety-cap deadline.
  const suppressScrollReadsRef = React.useRef(false);
  const suppressUntilMsRef = React.useRef(0);

  const handleScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const pinned = isPinnedToBottom(event.currentTarget);
    if (suppressScrollReadsRef.current) {
      // Lift suppression once the glide arrives (pinned) or the cap elapses;
      // until then ignore the animation's own intermediate scroll events.
      if (pinned || performance.now() >= suppressUntilMsRef.current) {
        suppressScrollReadsRef.current = false;
      } else {
        return;
      }
    }
    isPinnedRef.current = pinned;
  }, []);

  // Sending a message is an explicit "follow along" signal: re-pin so the
  // auto-follow effect carries the view down to the bubble the user just sent,
  // even if they had scrolled up to read history.
  const handleSend = React.useCallback(
    (content: string, attachments?: IObservationDocument[]) => {
      isPinnedRef.current = true;
      smoothFollowOnceRef.current = true;
      onSend(content, attachments);
    },
    [onSend]
  );

  // One pinned-aware auto-follow for both discrete new bubbles (length changes)
  // and token-level streaming, where a bubble's text grows in place with no
  // length change. Key off total content length, not the tail bubble's — the
  // streaming bubble is not always last (mid-turn queued user bubbles sit below
  // it), so a tail-only key would stall the follow. Streaming jumps instantly —
  // smooth scrolling would pile up animations at token rate — but a send sets
  // `smoothFollowOnceRef` so the user's own bubble glides into view once.
  const totalContentLength = messages.reduce((sum, m) => sum + m.content.length, 0);
  React.useEffect(() => {
    if (!isPinnedRef.current) {
      return;
    }
    const container = scrollContainerRef.current;
    if (container) {
      if (smoothFollowOnceRef.current) {
        smoothFollowOnceRef.current = false;
        // Mask the animation's own scroll events until it settles at the bottom
        // (see `suppressScrollReadsRef`), so a delta landing mid-glide doesn't
        // read as the user scrolling away and cancel the follow.
        suppressScrollReadsRef.current = true;
        suppressUntilMsRef.current = performance.now() + SMOOTH_FOLLOW_SETTLE_TIMEOUT_MS;
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages.length, totalContentLength]);

  const isConnecting = connectionStatus === 'connecting';
  const isConnected = connectionStatus === 'connected';
  const isSuperseded = connectionStatus === 'superseded';
  // The superseded state has its own banner (with a re-claim action), so keep
  // it out of the generic error block even though its own `error` may be set.
  const hasError = !isSuperseded && (connectionStatus === 'error' || !!error);

  // While a round's text streams, its "working" affordance lives inline at the
  // bottom of the growing bubble (see `AssistantMessage`), so the standalone
  // spinner below is suppressed. Between rounds (tools running, or before the
  // first token) no bubble is streaming and the standalone spinner shows.
  const isStreaming = messages.some((m) => m.isStreaming === true);

  const dividers = React.useMemo(() => buildTranscriptDividers(messages), [messages]);

  // Ids of bubbles that continue a same-sender run (no divider) — the view tucks
  // them tight under the previous bubble so a burst from one side reads as a
  // group, not evenly-spaced singles.
  const groupedIds = React.useMemo(() => computeGroupedMessageIds(messages, dividers), [messages, dividers]);

  // Split the transcript so the thinking spinner sits between in-flight
  // messages (the current turn) and queued messages (the user typed them
  // mid-turn; they wait for the current turn's reply before being batched
  // into the next turn). Confirmed + in-flight rows go above the spinner;
  // queued rows go below.
  //
  // The split point is the first row that is pending AND not in flight —
  // i.e. the first queued bubble. Without this, B and C typed while A is
  // being processed would visually sit above the spinner, misleading the
  // user into thinking the assistant is already working on C when it is
  // still on A.
  const firstQueuedIndex = messages.findIndex((m) => m.isPending === true && m.isInFlight !== true);
  const messagesAboveThinking = firstQueuedIndex === -1 ? messages : messages.slice(0, firstQueuedIndex);
  const messagesBelowThinking = firstQueuedIndex === -1 ? [] : messages.slice(firstQueuedIndex);

  const renderMessageRow = (message: IConversationMessage) => {
    if (message.kind === 'tool-call') {
      return <ToolCallLine key={message.id} goal={message.content} />;
    }
    if (!isRenderableMessage(message)) {
      return null;
    }
    const divider = dividers.get(message.id);
    return (
      <div
        key={message.id}
        // Tuck a same-sender continuation tight under the previous bubble (the
        // container's gap-4 is otherwise applied between every row). Only user
        // bubbles tuck — bubble-less assistant prose would weld into one wall of
        // text with no boundary to separate consecutive turns.
        className={cx('flex flex-col gap-1', groupedIds.has(message.id) && message.role === 'user' && '-mt-2.5')}
      >
        {divider?.kind === 'session' ? (
          // Date-stamped rule opening a new sitting: a hairline on each side with
          // the label vertically centered on the line.
          <div className="flex items-center gap-3 py-2">
            <div className="border-secondary flex-1 border-t" />
            <span className="text-tertiary shrink-0 text-xs">{divider.label}</span>
            <div className="border-secondary flex-1 border-t" />
          </div>
        ) : divider?.kind === 'minute' ? (
          <div className="text-tertiary py-2 text-center text-xs">{divider.label}</div>
        ) : null}
        <AssistantMessage message={message} />
      </div>
    );
  };

  // Render a slice of the transcript. Consecutive tool-call lines render in a
  // single tight-gap cluster (gap-1) so they read as one activity group, while
  // the cluster keeps the normal gap-4 from its neighbors. The clustering (and
  // the dropping of empty rows that would otherwise split a cluster) is a pure,
  // unit-tested helper — see `clusterTranscriptRows`.
  const renderRows = (rows: IConversationMessage[]): React.ReactNode[] =>
    clusterTranscriptRows(rows).map((item) =>
      item.type === 'tool-cluster' ? (
        <div key={`tool-calls-${item.toolCalls[0].id}`} className="flex flex-col gap-1">
          {item.toolCalls.map(renderMessageRow)}
        </div>
      ) : (
        renderMessageRow(item.message)
      )
    );

  const inputPlaceholder = isSuperseded
    ? 'Open in another tab or window — choose "Reconnect" to continue'
    : isConnecting
      ? 'Connecting...'
      : isConnected
        ? "Ask about your family's finances..."
        : 'Connection paused...';

  return (
    <div className="flex h-full w-full flex-col">
      {/* Header — to start a fresh session, the user types the `/reset` command. */}
      <div className="border-secondary flex items-center gap-3 border-b px-6 py-4">
        <div className="text-primary text-xl font-semibold">Mantle Assistant</div>
        <ConnectionIndicator status={connectionStatus} />
      </div>

      {/* Messages area — horizontal padding lives on the scroll container (not
          the inner column) so the max-w-3xl content box lands on the same left/
          right edge as the composer, whose wrapper carries the matching px-4. */}
      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 py-6">
          {/* Error message */}
          {hasError && (
            <div className="bg-error-primary text-error-primary rounded-lg p-4 text-sm">
              {error ?? 'Connection interrupted. Reconnecting while this tab is active.'}
            </div>
          )}

          {/* Loading spinner — covers both the cold-connect window (WS not
              yet open, often gated behind AuthProvider resolving session)
              AND the post-open rehydration handshake
              (`transcript_snapshot` + `assistant_ready`). Suppressed once
              local messages populate so a reconnect with stale messages
              keeps showing them until the fresh snapshot replaces. */}
          {(isConnecting || isRehydrating) && messages.length === 0 && !hasError && (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <LoadingIndicator size="md" />
              <div className="text-tertiary text-sm">Loading your conversation…</div>
            </div>
          )}

          {/* Empty state */}
          {messages.length === 0 && !isProcessing && !hasError && !isConnecting && !isRehydrating && !isSuperseded && (
            <AssistantPaneEmptyState />
          )}

          {/* Messages up to and including the message currently being
              processed (the oldest pending). */}
          {renderRows(messagesAboveThinking)}

          {/* A turn in flight with no text currently streaming (tools running,
              or before the first token): a bare spinner + animated "Thinking…"
              label on its own line — NOT in a bubble — sitting beneath any
              tool-activity lines (which are in `messagesAboveThinking`) and
              above any queued follow-ups. Suppressed while a round's text streams
              (that round's spinner lives inline in its growing bubble), and while
              a client tool awaits the browser (the user is mid-Plaid-flow — the
              tool-activity line already explains the wait). */}
          {isProcessing && !isStreaming && !isAwaitingClientTool && <ThinkingIndicator />}

          {/* User messages queued behind the current turn. Each is an
              optimistic bubble that hasn't yet been picked up by the server
              orchestrator's FIFO. */}
          {renderRows(messagesBelowThinking)}
        </div>
      </div>

      {/* Superseded — Mantle Assistant is live in another tab/window. A thin
          bar pinned just above the composer (outside the scroll container, so it
          never scrolls away) offering an explicit re-claim. This tab never
          auto-reconnects — that would evict the other one in a loop. */}
      {isSuperseded && (
        <div className="border-secondary bg-warning-primary text-warning-primary flex items-center justify-center gap-1.5 border-t px-6 py-2 text-sm">
          <span>Mantle Assistant is open in another tab.</span>
          <button
            type="button"
            onClick={onReclaim}
            className="cursor-pointer font-semibold underline underline-offset-2 hover:no-underline"
          >
            Reconnect
          </button>
          <span>to continue (will disconnect other tab)</span>
        </div>
      )}

      {/* Input area */}
      <div className="border-secondary border-t p-4">
        <div className="mx-auto max-w-3xl">
          {/* Input is always enabled. The hook queues sends when the WS is
              not OPEN + assistant_ready (drained on the next ready frame),
              and the server orchestrator FIFOs mid-turn user_messages on
              its own processing chain, so backpressure does not need a UI
              gate. The placeholder still reflects the underlying network
              state. */}
          <AssistantInput
            onSend={handleSend}
            isAbortable={isAbortable}
            onStop={onStop}
            placeholder={inputPlaceholder}
          />
        </div>
      </div>
    </div>
  );
};

export default AssistantPaneView;
